import { Modal } from '@mui/material'
import { useEffect, useState } from 'react'
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../../store/store'
import { updateUserPersonalDetails } from '../../../../../../../store/reducers/userReducer'
import {
  AddressWrapper,
  StyledBackBtn,
  StyledBtnContainer,
  StyledImageContentContainer,
  StyledMiniTitle,
  StyledModal,
  StyledModalName,
  StyledModalNameDob,
  StyledModalTop,
  StyledTitelContainer,
  StyledTitle,
} from './style'
import Dropdown from '../../../../../../../shared/components/dropdown/Dropdown'
import citiesJson from '../../../../../../../assets/data/cities.json'
import { NumberInput } from '../../../../../../../shared/components/number-input/NumberInput'
import {
  UpdateUserRequest,
  useUpdateSfUserMutation,
} from '../../../../../../../store/api-slices/userSlice'
import { Error } from 'shared/components/error/Error'
import { useGenericFormState } from 'shared/hooks/useGenericState'
import { setFormTranslatedText } from 'pages/auth/utils/setFormTranslatedText'
import { useTranslation } from 'react-i18next'
import { IAddress } from 'types/interfaces/IAddress'
import { TextInput } from 'shared/components/text-input/TextInput'
import { ELanguage } from 'types/enums/ELanguage'
import { Button } from 'shared/components/button/Button'
import { DatePickerWrapper } from 'shared/components/date-picker/DatePickerWrapper'
import { IUser } from 'types/interfaces/IUser'
import { Cities } from 'types/types/Cities'
import { IPersonalDetails } from 'types/interfaces/IPersonalDetails'
import { useMediaQuery } from '@mui/material'
import { device } from 'constants/media-query'
import rightarrowbtn from 'assets/icons/tabler_chevron-right.svg'
import closebtn from 'assets/icons/close.svg'
import { GenericSnackbar } from 'shared/components/snackbar/GenericSnackbar'
import { FavPlayerImage } from '../playerImage/FavPlayerImage'
import { Loader } from 'shared/components/loader/loader'

type PersonalInfoFormData = IAddress & {
  firstName: string
  lastName: string
  playerImage?: number
}

interface PersonalDetailsModalProps {
  userDetails: IPersonalDetails
  isOpen: boolean
  onClose: () => void
}

type PersonalInfoFormErrors = Partial<
  Record<keyof PersonalInfoFormData, boolean>
>

export const PersonalDetailsModal = ({
  userDetails,
  isOpen,
  onClose,
}: PersonalDetailsModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const directions = useAppSelector((state) => state.layout.language.direction)
  const [updateSfUser] = useUpdateSfUserMutation()
  const address = useAppSelector<IAddress | undefined>(
    (state) => state.user.personalDetails?.addresses?.[0],
  )
  const user = useAppSelector<IUser>((state) => state.user)
  const { t, i18n } = useTranslation('', {
    keyPrefix: 'pages.personalArea.tabs.personalDetails',
  })
  setFormTranslatedText('signUp.personalDetails')
  const [personalInfoFormData, setPersonalInfoFormData] =
    useGenericFormState<PersonalInfoFormData>({
      firstName: user?.personalDetails?.firstName ?? '',
      lastName: user?.personalDetails?.lastName ?? '',
      playerImage: user?.personalDetails?.playerImage ?? undefined,
      city: address?.city,
      street: address?.street,
      buildingNumber: address?.buildingNumber,
    })
  const isDesktop = useMediaQuery(device.desktop)
  const [errors, setErrors] = useState<PersonalInfoFormErrors>({
    firstName: false,
    lastName: false,
    playerImage: false,
    city: false,
    street: false,
    buildingNumber: false,
  })

  const [cities, setCities] = useState<Cities>({})
  const [streets, setStreets] = useState<string[]>([])
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [submitError, setSubmitError] = useState<boolean>(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cityHebName, setCityHebName] = useState<string>()

  useEffect(() => {
    setCities(citiesJson)
    setPersonalInfoFormData({
      ...personalInfoFormData,
      city: address?.city,
      street: address?.street,
      buildingNumber: address?.buildingNumber,
    })
  }, [t])

  useEffect(() => {
    const isFormDataComplete = Object.entries(personalInfoFormData).every(
      ([key, value]) => key === 'playerImage' || !!value,
    )
    const isValid = Object.values(errors).every((error) => !error)
    if (isFormDataComplete && isValid) {
      setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }
  }, [Object.values(personalInfoFormData), errors])

  const handleFieldChange = <T extends keyof typeof personalInfoFormData>(
    field: T,
    value?: (typeof personalInfoFormData)[T],
    error?: boolean,
  ) => {
    setErrors({ ...errors, [field]: error })
    setPersonalInfoFormData({ ...personalInfoFormData, [field]: value })
  }

  const handleCityFieldChange = (value: string, error?: boolean) => {
    setErrors({ ...errors, city: error })
    setPersonalInfoFormData({
      ...personalInfoFormData,
      city: value,
      street: '',
    })
  }

  const handleEditClick = () => {
    onClose()
  }

  const handleSaveChanges = async () => {
    setIsLoading(true)
    try {
      const updateSfUserReq: UpdateUserRequest = {
        firstName: personalInfoFormData?.firstName ?? '',
        lastName: personalInfoFormData?.lastName ?? '',
        street: personalInfoFormData?.street ?? '',
        buildingNumber: personalInfoFormData.buildingNumber ?? '',
        city: personalInfoFormData.city ?? '',
        playerImage: personalInfoFormData.playerImage,
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      await updateSfUser(updateSfUserReq).unwrap()
      dispatch(
        updateUserPersonalDetails({
          firstName: updateSfUserReq.firstName,
          lastName: updateSfUserReq.lastName,
          playerImage: updateSfUserReq.playerImage,
          addresses: [
            {
              city: updateSfUserReq.city,
              street: updateSfUserReq.street,
              buildingNumber: updateSfUserReq.buildingNumber,
            },
          ],
        }),
      )
      setIsLoading(false)
      onClose()
      setSnackbarType('success')
      setSnackbarOpen(true)
    } catch (error) {
      onClose()
      setSnackbarType('error')
      setSnackbarOpen(true)
    }
  }

  useEffect(() => {
    const citiesArray = Object.values(cities)
    const fieldName = i18n.language === ELanguage.HEBREW ? 'hebName' : 'enName'

    const matchingCity = citiesArray.find(
      (city) => city[fieldName] === personalInfoFormData.city,
    )
    setCityHebName(matchingCity?.hebName || '')
    const streetsArray = matchingCity?.streets || []
    setStreets(streetsArray)
  }, [personalInfoFormData.city, cities, i18n.language])

  useEffect(() => {
    // Reset values when modal is closed with cancel button
    if (!isOpen) {
      setPersonalInfoFormData({
        firstName: user?.personalDetails?.firstName ?? '',
        lastName: user?.personalDetails?.lastName ?? '',
        playerImage: user?.personalDetails?.playerImage ?? undefined,
        city: address?.city,
        street: address?.street,
        buildingNumber: address?.buildingNumber,
      })

      setErrors({
        firstName: false,
        lastName: false,
        city: false,
        street: false,
      })
    }
  }, [isOpen, user, address, t])

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarType, setSnackbarType] = useState<'success' | 'error'>(
    'success',
  )
  const snackbarMessage =
    snackbarType === 'success'
      ? t('actions.snackbar.success')
      : t('actions.snackbar.error')

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        {isLoading ? (
          <Loader isLoading={isLoading} />
        ) : (
          <StyledModal
            style={{
              background: 'white',
              borderRadius: '4px',
              padding: '24px 20px 40px',
              position: 'relative',
              margin: '0 auto',
              direction: directions,
              maxWidth: isDesktop ? 'max-content' : '85dvw',
            }}
          >
            <>
              <StyledTitelContainer>
                <StyledTitle style={{ textAlign: 'start' }}>
                  {t('editPersonalDetails')}
                </StyledTitle>
                <StyledBackBtn onClick={handleEditClick}>
                  <img
                    src={isDesktop ? closebtn : rightarrowbtn}
                    alt="edit-info"
                  />
                </StyledBackBtn>
              </StyledTitelContainer>
              <StyledModalTop>
                <StyledModalNameDob>
                  <StyledMiniTitle>{t('details.nameAndDob')}</StyledMiniTitle>
                  <StyledModalName>
                    <TextInput
                      className={'personal-details-edit'}
                      isRequired={true}
                      value={personalInfoFormData.firstName}
                      minLength={2}
                      maxLength={40}
                      regex={RegExp(/^[a-zA-Z\u0590-\u05FF .,'׳-]*$/i)}
                      hasError={errors.firstName}
                      id={'firstName'}
                      label={t('details.firstName')}
                      errorText={t('inputs.firstName.errorText')}
                      callback={(value, error) =>
                        handleFieldChange('firstName', value, error)
                      }
                    />
                    <TextInput
                      className={'personal-details-edit'}
                      isRequired={true}
                      value={
                        personalInfoFormData.lastName !== 'temp'
                          ? personalInfoFormData.lastName
                          : ''
                      }
                      minLength={2}
                      maxLength={40}
                      regex={RegExp(/^[a-zA-Z\u0590-\u05FF .,'׳-]*$/i)}
                      hasError={errors.lastName}
                      id={'lastName'}
                      label={t('details.lastName')}
                      errorText={t('inputs.lastName.errorText')}
                      callback={(value, error) =>
                        handleFieldChange('lastName', value, error)
                      }
                    />
                  </StyledModalName>
                  <DatePickerWrapper
                    className={'personal-details-edit'}
                    isRequired={false}
                    disabled={true}
                    value={userDetails.dob}
                    labelText={t('details.dob')}
                    errorText={t('inputs.dob.errorText')}
                    callback={(value, error) => console.log({ value, error })}
                  />
                </StyledModalNameDob>
                <StyledImageContentContainer>
                  <FavPlayerImage
                    userDetails={userDetails}
                    isEdit={true}
                    onChange={async (playerImage) =>
                      handleFieldChange('playerImage', playerImage)
                    }
                  />
                </StyledImageContentContainer>
              </StyledModalTop>
              <StyledMiniTitle>{t('details.address')}</StyledMiniTitle>
              <AddressWrapper>
                <Dropdown
                  className={'personal-details-edit'}
                  isRequired={true}
                  value={personalInfoFormData.city}
                  disabled={false}
                  hasError={errors.city}
                  id={'city'}
                  label={t('details.city')}
                  errorText={t('inputs.city.errorText')}
                  options={Object.values(cities).map(
                    (city) =>
                      city[
                        i18n.language === ELanguage.HEBREW
                          ? 'hebName'
                          : 'enName'
                      ],
                  )}
                  callback={handleCityFieldChange}
                />
                {i18n.language === ELanguage.HEBREW ? (
                  <Dropdown
                    className={'personal-details-edit'}
                    isRequired={true}
                    value={personalInfoFormData.street}
                    disabled={false}
                    hasError={errors.street}
                    id={'street'}
                    label={t('details.street')}
                    errorText={t('inputs.street.errorText')}
                    options={streets}
                    callback={(value, error) =>
                      handleFieldChange('street', value, error)
                    }
                  />
                ) : (
                  <TextInput
                    className={'personal-details-edit'}
                    isRequired={true}
                    value={personalInfoFormData.street}
                    disabled={false}
                    hasError={errors.street}
                    id={'street'}
                    minLength={2}
                    maxLength={40}
                    regex={RegExp(/^[a-zA-Z\u0590-\u05FF .,'׳-]*$/i)}
                    label={t('details.street')}
                    errorText={t('inputs.street.foreignErrorText')}
                    callback={(value, error) =>
                      handleFieldChange('street', value, error)
                    }
                  />
                )}
                <NumberInput
                  className={'personal-details-edit'}
                  isRequired={true}
                  value={personalInfoFormData.buildingNumber ?? undefined}
                  regex={RegExp(/^[0-9]{1,9}$/i)}
                  hasError={errors.buildingNumber}
                  errorText={t('inputs.buildingNumber.errorText')}
                  callback={(value, error) =>
                    handleFieldChange('buildingNumber', value, error)
                  }
                  id={'buildingNumber'}
                  label={t('details.buildingNumber')}
                ></NumberInput>
              </AddressWrapper>

              {submitError && (
                <Error
                  hasError={true}
                  errorText={t('buttons.submit.errorText')}
                />
              )}
              <StyledBtnContainer>
                {/* <StyledSaveBtn> */}
                <Button
                  type="submit"
                  disabled={isDisabled}
                  className={`button ${isDisabled ? 'disabled' : ''}`}
                  text={t('actions.save')}
                  callback={() => handleSaveChanges()}
                  style={{ height: '48px' }}
                />
                {/* </StyledSaveBtn> */}

                <Button
                  type="reset"
                  className={`button`}
                  text={t('actions.cancel')}
                  callback={() => onClose()}
                  style={{ height: '48px' }}
                />
              </StyledBtnContainer>
            </>
          </StyledModal>
        )}
      </Modal>
      <GenericSnackbar
        severity={snackbarType}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  )
}
